import { useCallback, useEffect, useRef, useState } from "react";

export type AsyncState<T> =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "success"; data: T }
  | { status: "error"; error: string };

export interface UseAsyncStateReturn<T> {
  state: AsyncState<T>;
  execute: (...args: unknown[]) => Promise<void>;
  reset: () => void;
}

export function useAsyncState<T>(
  asyncFn: (signal: AbortSignal) => Promise<T>,
  options?: { immediate?: boolean },
): UseAsyncStateReturn<T> {
  const [state, setState] = useState<AsyncState<T>>({ status: "idle" });
  const acRef = useRef<AbortController | null>(null);
  const fnRef = useRef(asyncFn);
  fnRef.current = asyncFn;

  const execute = useCallback(async () => {
    acRef.current?.abort();
    const ac = new AbortController();
    acRef.current = ac;
    setState({ status: "loading" });

    try {
      const data = await fnRef.current(ac.signal);
      if (!ac.signal.aborted) {
        setState({ status: "success", data });
      }
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
      if (!ac.signal.aborted) {
        setState({
          status: "error",
          error: err instanceof Error ? err.message : "Unknown error",
        });
      }
    }
  }, []);

  const reset = useCallback(() => {
    acRef.current?.abort();
    setState({ status: "idle" });
  }, []);

  useEffect(() => {
    if (options?.immediate) {
      void execute();
    }
    return () => { acRef.current?.abort(); };
  }, [execute, options?.immediate]);

  return { state, execute, reset };
}
